import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { roomHierarchyMap } from "./utils/roomDeviceMap";
import { generateObservationURL } from "./utils/aesEncryptor2";
import logo2 from "./logo2.png";
import "./PatientDashboard.css";

// Look up a device id by room name across all facilities/units
const findDeviceId = (roomName) => {
  for (const facility of Object.values(roomHierarchyMap)) {
    for (const unit of Object.values(facility)) {
      if (unit[roomName]) return unit[roomName];
    }
  }
  return null;
};

const Sparkline = ({ values, color = "#6fb6ff", width = 70, height = 24 }) => {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);

  const points = values
    .map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(" ");

  const lastX = (values.length - 1) * step;
  const lastY =
    height - ((values[values.length - 1] - min) / range) * (height - 4) - 2;

  return (
    <svg width={width} height={height} className="pd-sparkline">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.7"
      />
      <circle cx={lastX} cy={lastY} r="2.5" fill={color} />
    </svg>
  );
};

const PATIENT = {
  name: "David Chen",
  sex: "Male",
  age: "81 y.o.",
  dob: "5/12/1944",
  mrn: "99001122",
  code: "FULL",
  room: "RH ICU 203",
  diagnosis: "Sepsis - UTI",
  allergies: "None",
  attending: "Dr. Halpert",
};

const MEDS = [
  { name: "Lisinopril",                  dose: "20 mg PO",  schedule: "Daily" },
  { name: "Metformin",                   dose: "500 mg PO", schedule: "BID" },
];

const RESPIRATORY = { mode: "Ventilator", fio2: "70%", spo2: "95", resp: "12" };

const HISTORY = [
  "Diabetes Mellitus Type 2",
  "Hypertension",
  "Chronic Kidney Disease (Stage 3)",
];

const VITALS = [
  { label: "BP",   value: "91/45", unit: "mmHg", color: "#ff5b5b",
    history: [88, 79, 101, 94, 86, 103, 92, 100] },
  { label: "Temp", value: "102.1",   unit: "°F",   color: "#fff",
    history: [100.1, 102.4, 99.9, 99.5, 100.6, 99.0, 101.2, 99.8] },
  { label: "Pulse",value: "99",     unit: "bpm",  color: "#fff",
    history: [113, 122, 105, 99, 111, 115, 109, 99] },
  { label: "Resp", value: "12",     unit: "/min", color: "#fff",
    history: [16, 14, 15, 13, 14, 15, 14, 14] },
  { label: "SpO₂", value: "97",     unit: "%",    color: "#fff",
    history: [96, 95, 94, 96, 95, 95, 94, 95] },
  { label: "Pain", value: "2",      unit: "/10",  color: "#ffb454",
    history: [7, 7, 6, 6, 5, 5, 4, 4] },
];

const LABS = [
  { name: "Glucose",    value: "137",  unit: "mg/dL",  trend: "up",
    history: [118, 125, 130, 128, 134, 132, 137] },
  { name: "Potassium",  value: "4.2",  unit: "mmol/L", trend: "flat",
    history: [4.1, 4.3, 4.2, 4.0, 4.2, 4.3, 4.2] },
  { name: "Sodium",     value: "139",  unit: "mmol/L", trend: "flat",
    history: [140, 138, 139, 140, 139, 138, 139] },
  { name: "Creatinine", value: "1.4",  unit: "mg/dL",  trend: "up",
    history: [1.2, 1.2, 1.3, 1.3, 1.4, 1.4, 1.4] },
  { name: "Hemoglobin", value: "11.2", unit: "g/dL",   trend: "down",
    history: [13.4, 12.8, 12.1, 11.6, 11.4, 11.3, 11.2] },
  { name: "WBC",        value: "8.4",  unit: "K/µL",   trend: "flat",
    history: [9.2, 9.0, 8.7, 8.6, 8.5, 8.4, 8.4] },
];

const TREND_COLOR = { up: "#ff5b5b", down: "#6fb6ff", flat: "#9aa2b1" };

// Realistic post-op day 2-3 picture for an 87 y.o. s/p right hip fracture
// repair with PMH of COPD, DM2, HTN, CKD-3
const UPCOMING = [
  { type: "lab",       name: "CBC w/ Diff",          when: "5/22  0600" },
  { type: "lab",       name: "BMP",                  when: "5/22  0600" },
  { type: "imaging",   name: "Right Hip X-ray AP/Lat", when: "5/22  0830" },
  { type: "consult",   name: "PT — Gait Eval",       when: "5/22  1000" },
  { type: "procedure", name: "Wound Check / Dsg Change", when: "5/22  1200" },
  { type: "consult",   name: "OT Evaluation",        when: "5/22  1330" },
  { type: "consult",   name: "Geriatrics Consult",   when: "5/22  1500" },
  { type: "lab",       name: "Hemoglobin Recheck",   when: "5/23  0600" },
  { type: "procedure", name: "Foley D/C Trial",      when: "5/23  0900" },
  { type: "imaging",   name: "TTE Echocardiogram",   when: "5/23  1100" },
  { type: "consult",   name: "SW / Discharge Planning", when: "5/24  1000" },
  { type: "consult",   name: "Care Conference",      when: "5/24  1400" },
];

const TYPE_LABEL = {
  lab: "LAB",
  imaging: "IMG",
  consult: "CONSULT",
  procedure: "PROC",
};

const LINES = [
  { type: "IV Peripheral", location: "Left Forearm 20G", date: "5/19/2026" },
  { type: "Foley Catheter", location: "Urethral 16 Fr.", date: "5/18/2026" },
];

const PatientDashboard = () => {
  const navigate = useNavigate();

  const [privacyActive, setPrivacyActive] = useState(false);
  const [privacyElapsed, setPrivacyElapsed] = useState(0);
  const [listening, setListening] = useState(false);
  const [talking, setTalking]     = useState(false);
  const [expanded, setExpanded]   = useState(false);

  const webcamRef = useRef(null);
  const streamRef = useRef(null);

  const room = PATIENT.room;

  const videoURL = useMemo(() => {
    const deviceId = findDeviceId(room);
    return deviceId ? generateObservationURL(deviceId, "9tH0WsxD1Ko") : null;
  }, [room]);

  // Privacy timer
  useEffect(() => {
    if (!privacyActive) {
      setPrivacyElapsed(0);
      return;
    }
    const startedAt = Date.now();
    const id = setInterval(() => {
      setPrivacyElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [privacyActive]);

  // Webcam — engage when mic toggles on, release when off
  useEffect(() => {
    let cancelled = false;

    if (talking) {
      navigator.mediaDevices
        .getUserMedia({ video: true, audio: false })
        .then((stream) => {
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          streamRef.current = stream;
          if (webcamRef.current) {
            webcamRef.current.srcObject = stream;
          }
        })
        .catch((err) => {
          console.error("Webcam access denied:", err);
          setTalking(false);
        });
    } else {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (webcamRef.current) webcamRef.current.srcObject = null;
    }

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [talking]);

  const formatElapsed = (sec) => {
    const pad = (n) => String(n).padStart(2, "0");
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  };

  return (
    <div className="patient-dashboard">
      {/* ───── Top patient bar ───── */}
      <div className="pd-topbar">
        <img src={logo2} className="pd-logo" alt="Verge" />
        <div className="pd-pt-block">
          <div className="pd-pt-name">{PATIENT.name}</div>
          <div className="pd-pt-meta">
            {PATIENT.sex}, {PATIENT.age} · DOB {PATIENT.dob}
          </div>
        </div>
        <div className="pd-pt-stats">
          <div><span className="pd-label">MRN</span> {PATIENT.mrn}</div>
          <div><span className="pd-label">Room</span> {PATIENT.room}</div>
          <div><span className="pd-label">Code</span> {PATIENT.code}</div>
          <div><span className="pd-label">Attending</span> {PATIENT.attending}</div>
          <div><span className="pd-label">Allergies</span> {PATIENT.allergies}</div>
        </div>
        <div className="pd-pt-diagnosis">
          <span className="pd-label">Dx</span> {PATIENT.diagnosis}
        </div>
        <button className="pd-back-btn" onClick={() => navigate("/patient-list")}>
          ← Back to Census
        </button>
      </div>

      {/* ───── Tile grid ───── */}
      <div className="pd-grid">
        {/* Vitals */}
        <div className="pd-tile pd-vitals">
          <div className="pd-tile-header">Vitals · Last 24h</div>
          <div className="pd-vitals-grid">
            {VITALS.map((v) => (
              <div key={v.label} className="pd-vital">
                <Sparkline values={v.history} color={v.color === "#fff" ? "#6fb6ff" : v.color} />
                <div className="pd-vital-text">
                  <div className="pd-vital-value" style={{ color: v.color }}>{v.value}</div>
                  <div className="pd-vital-label">
                    {v.label} <span className="pd-vital-unit">{v.unit}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Respiratory */}
        <div className="pd-tile pd-respiratory">
          <div className="pd-tile-header">Respiratory</div>
          <div className="pd-resp-grid">
            <div><span className="pd-mini-label">Mode</span><span className="pd-mini-value">{RESPIRATORY.mode}</span></div>
            <div><span className="pd-mini-label">FiO₂</span><span className="pd-mini-value">{RESPIRATORY.fio2}%</span></div>
            <div><span className="pd-mini-label">SpO₂</span><span className="pd-mini-value">{RESPIRATORY.spo2}%</span></div>
            <div><span className="pd-mini-label">Resp Rate</span><span className="pd-mini-value">{RESPIRATORY.resp}</span></div>
          </div>
        </div>

        {/* Camera */}
        <div className={`pd-tile pd-camera ${expanded ? "pd-camera--expanded" : ""}`}>
          <div className="pd-tile-header pd-camera-header">
            <span>Room Camera — {room}</span>
            <button
              type="button"
              className="pd-expand-btn"
              title={expanded ? "Restore" : "Expand"}
              onClick={() => setExpanded((e) => !e)}
            >
              {expanded ? (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                     stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                     stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                </svg>
              )}
            </button>
          </div>

          <div className="video-frame-wrapper">
            {videoURL ? (
              <iframe
                title="Patient Room Camera"
                src={videoURL}
                allow="camera; microphone"
                frameBorder="0"
                className="video-frame"
              />
            ) : (
              <div className="video-placeholder">Camera unavailable for {room}</div>
            )}

            {/* Self-view (PiP) — appears when mic is on */}
            {talking && (
              <div className="pd-self-view">
                <video ref={webcamRef} autoPlay muted playsInline />
                <div className="pd-self-label">Self View</div>
              </div>
            )}

            {/* Privacy curtain */}
            {privacyActive && (
              <div className="privacy-overlay">
                <svg viewBox="0 0 24 24" width="48" height="48" fill="none"
                     stroke="currentColor" strokeWidth="1.5"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3h18v2H3z" />
                  <path d="M5 5v15M19 5v15" />
                  <path d="M9 5c0 4-3 6-3 9 0 2 1 4 3 5" />
                  <path d="M15 5c0 4 3 6 3 9 0 2-1 4-3 5" />
                  <path d="M3 20h18" />
                </svg>
                <span>Privacy Mode Active</span>
                <span className="privacy-timer">{formatElapsed(privacyElapsed)}</span>
              </div>
            )}

            {/* Control bar */}
            <div className="video-overlay-controls">
              <button
                type="button"
                className={`overlay-btn ${privacyActive ? "overlay-btn--active" : ""}`}
                title={privacyActive ? "Exit Privacy Mode" : "Privacy Mode / Curtain"}
                onClick={() => setPrivacyActive((p) => !p)}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                     stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3h18v2H3z" />
                  <path d="M5 5v15M19 5v15" />
                  <path d="M9 5c0 4-3 6-3 9 0 2 1 4 3 5" />
                  <path d="M15 5c0 4 3 6 3 9 0 2-1 4-3 5" />
                  <path d="M3 20h18" />
                </svg>
              </button>

              <button
                type="button"
                className={`overlay-btn ${listening ? "overlay-btn--active" : ""}`}
                title={listening ? "Mute Audio from Room" : "Listen to Audio from Room"}
                onClick={() => setListening((l) => !l)}
              >
                {listening ? (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                       stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                       stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                )}
              </button>

              <button
                type="button"
                className={`overlay-btn ${talking ? "overlay-btn--active" : ""}`}
                title={talking ? "End 2-Way Call (Mic Off)" : "Start 2-Way Call (Mic On)"}
                onClick={() => setTalking((t) => !t)}
              >
                {talking ? (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                       stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="2" width="6" height="12" rx="3" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="22" />
                    <line x1="8" y1="22" x2="16" y2="22" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
                       stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <line x1="2" y1="2" x2="22" y2="22" />
                    <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
                    <path d="M15 9.34V5a3 3 0 0 0-5.94-.6" />
                    <path d="M17 16.95A7 7 0 0 1 5 12v-2" />
                    <path d="M19 10v2a7 7 0 0 1-.11 1.23" />
                    <line x1="12" y1="19" x2="12" y2="22" />
                    <line x1="8" y1="22" x2="16" y2="22" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Medications */}
        <div className="pd-tile pd-meds">
          <div className="pd-tile-header">Medications</div>
          <ul className="pd-list">
            {MEDS.map((m) => (
              <li key={m.name}>
                <div className="pd-list-primary">{m.name}</div>
                <div className="pd-list-secondary">{m.dose} · {m.schedule}</div>
              </li>
            ))}
          </ul>
        </div>

        {/* History */}
        <div className="pd-tile pd-history">
          <div className="pd-tile-header">PMH</div>
          <ul className="pd-list pd-list--simple">
            {HISTORY.map((h) => <li key={h}>{h}</li>)}
          </ul>
        </div>

        {/* Lines/Drains/Airways */}
        <div className="pd-tile pd-lines">
          <div className="pd-tile-header">Lines, Drains & Airways</div>
          <ul className="pd-list">
            {LINES.map((l) => (
              <li key={l.type}>
                <div className="pd-list-primary">{l.type}</div>
                <div className="pd-list-secondary">{l.location} · placed {l.date}</div>
              </li>
            ))}
          </ul>
        </div>

        {/* Lab Trends */}
        <div className="pd-tile pd-labs">
          <div className="pd-tile-header">Lab Trends · Last 7d</div>
          <table className="pd-labs-table">
            <tbody>
              {LABS.map((lab) => (
                <tr key={lab.name}>
                  <td>{lab.name}</td>
                  <td className="pd-lab-spark">
                    <Sparkline
                      values={lab.history}
                      color={TREND_COLOR[lab.trend]}
                      width={56}
                      height={20}
                    />
                  </td>
                  <td className="pd-lab-value">{lab.value} <span>{lab.unit}</span></td>
                  <td className={`pd-lab-trend pd-lab-trend--${lab.trend}`}>
                    {lab.trend === "up" ? "▲" : lab.trend === "down" ? "▼" : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="pd-footer">
        <div className="pd-footer-header">
          Upcoming Orders, Consults & Procedures
        </div>
        <div className="pd-footer-strip">
          {UPCOMING.map((item) => (
            <div
              key={`${item.name}-${item.when}`}
              className={`pd-upcoming-card pd-upcoming--${item.type}`}
            >
              <div className="pd-upcoming-type">{TYPE_LABEL[item.type]}</div>
              <div className="pd-upcoming-name">{item.name}</div>
              <div className="pd-upcoming-when">{item.when}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PatientDashboard;